/**
 * <InstallPrompt> — non-intrusive "Add to home screen" hint for PWA-installable
 * browsers (Chrome/Edge/Samsung/Brave/Opera/etc.). On Safari, the user has to
 * trigger the install themselves via Share > Add to Home Screen — there's no
 * programmatic API. The prompt suppresses itself on iOS to avoid teasing
 * users with a button that doesn't work.
 *
 * Behaviour
 * ---------
 *   - Does NOT show on the user's first visit. We track visit count in
 *     localStorage and only show after the second visit, so we don't ambush
 *     a user who's just landed and is trying to read.
 *   - Listens for the `beforeinstallprompt` event and stashes it. When the
 *     user clicks "Install", we call `prompt()` on the stashed event.
 *   - If the user dismisses, we remember and never show again on this device.
 *
 * Color-agnostic — pass `className` to override the default neutral styling.
 */

import React, { useEffect, useState } from 'react';

const VISIT_COUNT_KEY = 'bc_pwa_visits';
const DISMISSED_KEY = 'bc_pwa_install_dismissed';

/**
 * @typedef {object} InstallPromptProps
 * @property {string} [appName]
 * @property {number} [minVisits]
 * @property {string} [className]
 */

export function InstallPrompt({
  appName = 'this app',
  minVisits = 2,
  className = '',
}) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  // Track visit count + decide whether to be eligible to show.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.localStorage.getItem(DISMISSED_KEY) === '1') return;

    const visits = Number(window.localStorage.getItem(VISIT_COUNT_KEY) || '0') + 1;
    window.localStorage.setItem(VISIT_COUNT_KEY, String(visits));
    if (visits >= minVisits) {
      // Eligible — but we still need a stashed beforeinstallprompt event before
      // we render. The event handler below sets `visible` if/when it fires.
    }
  }, [minVisits]);

  // Listen for the install-prompt opportunity.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = (event) => {
      // Prevent the default mini-infobar from popping; we'll trigger it ourselves.
      event.preventDefault();
      const visits = Number(window.localStorage.getItem(VISIT_COUNT_KEY) || '0');
      if (
        visits >= minVisits &&
        window.localStorage.getItem(DISMISSED_KEY) !== '1'
      ) {
        setDeferredPrompt(event);
        setVisible(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [minVisits]);

  if (!visible || !deferredPrompt) return null;

  const onInstall = async () => {
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } finally {
      setDeferredPrompt(null);
      setVisible(false);
    }
  };

  const onDismiss = () => {
    window.localStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
    setDeferredPrompt(null);
  };

  return (
    <div
      role="dialog"
      aria-labelledby="bc-install-prompt-title"
      className={`fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-install-prompt rounded-lg border border-slate-300 bg-white shadow-lg p-4 pb-safe-bottom ${className}`}
    >
      <h3 id="bc-install-prompt-title" className="text-base font-semibold text-slate-900 mb-1">
        Install {appName}
      </h3>
      <p className="text-sm text-slate-600 mb-3">
        Add it to your home screen for faster access and offline support.
      </p>
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onDismiss}
          className="min-h-touch px-3 py-2 text-sm text-slate-600 hover:text-slate-900"
        >
          Not now
        </button>
        <button
          type="button"
          onClick={onInstall}
          className="min-h-touch px-4 py-2 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800"
        >
          Install
        </button>
      </div>
    </div>
  );
}
